import {
  Example,
  ForgotPassword,
  Home,
  Login,
  FukumaruQuizList,
  PostDetail,
  PostFukumaruDetail,
  PostFukumaruList,
  PostPage,
  ResetPassword,
  SpecialDayDetail,
  SpecialDayForm,
  SpecialDayList,
  UserDetail,
  UserPage,
  VersionList,
  GameIntroductionList,
  AyumiList,
  AyumiForm,
} from '@/pages';
import { UserUpdatePage } from '@/pages/User/UserForm';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { APP_ROUTER } from './routes';
import { NotificationList } from '@/pages/Notification';
import { PromptDetail, PromptForm, PromptList } from '@/pages/Prompt';
import { EventList } from '@/pages/Event';
import { BannerList } from '@/pages/Banner/BannerList';
import { ChatFukumaruList } from '@/pages/ChatFukumaru';
import { ContentList } from '@/pages/Content';
import { SurveyList } from '@/pages/Survey';
import { NotificationRemindList } from '@/pages/NotificationRemind';
import { SurveyHealthList } from '@/pages/SurveyHealth';
import { SurveyHealthQuestionList } from '@/pages/SurveyHealthQuestion';
import { CalendarPostList } from '@/pages/CalendarPost';
import { PartnerList } from '@/pages/Partner/PartnerList';
import { CompanyList } from '@/pages/Company/CompanyList/index';
import { CompanyCreate } from '@/pages/Company/CompanyCreate/index';
import { CompanyDetail } from '@/pages/Company/CompanyDetail/index';
import { ArticleList } from '@/pages/Article/ArticleList';
import { CategoryList } from '@/pages/Category/CategoryList';
import { CogevoStampImageList } from '@/pages/CogevoStampImage';
import { ScreenMediaList } from '@/pages/ScreenMedia';
import { IllustrationThemeList } from '@/pages/IllustrationTheme';
import { OfferList } from '@/pages/Offer/OfferList';
import { InquiryList } from '@/pages/Inquiry';
import {
  KazokugotoCharacterList,
  KazokugotoGenerate,
  KazokugotoItemList,
  KazokugotoPromptList,
  KazokugotoTopicList,
} from '@/pages/Kazokugoto';
import {
  AgentFlow,
  AgentHenkaCoverage,
  AgentMessageList,
  AgentPromptList,
  AgentRun,
} from '@/pages/Agent';
import { FamilyList } from '@/pages/Family';
import { Dashboard } from '@/pages/Dashboard';
import { KpiInitiatives } from '@/pages/KpiInitiatives';
import { AppUsage } from '@/pages/AppUsage';
import { CompanyReport } from '@/pages/CompanyReport';
import { AdminUserList } from '@/pages/AdminUser';
import { useAuth } from '@/providers/Auth';
import {
  ADMIN_ONLY,
  DASHBOARD_READERS,
  PermissionKey,
  hasAnyPermission,
} from '@/utils/permission';

export interface IRouteWrapProps {
  isAuthenticated: boolean;
}

export const publicRoutes = [
  {
    path: APP_ROUTER.AUTH.LOGIN,
    element: <Login title='Login' />,
  },
  {
    path: APP_ROUTER.AUTH.FORGOT_PASSWORD,
    element: <ForgotPassword title='Forgot Password' />,
  },
  {
    path: APP_ROUTER.AUTH.RESET_PASSWORD,
    element: <ResetPassword title='Reset Password' />,
  },
];

interface IPrivateRoute {
  path: string;
  element: React.ReactNode;
  /** 省略時は管理者専用（ADMIN_ONLY）。空配列ならログインしていれば誰でも */
  permissions?: PermissionKey[];
}

export const privateRoutes: IPrivateRoute[] = [
  {
    path: APP_ROUTER.HOME,
    element: <Home title='ダッシュボード' />,
    permissions: [],
  },
  {
    path: APP_ROUTER.DASHBOARD,
    element: <Dashboard title='ダッシュボード' />,
    permissions: DASHBOARD_READERS,
  },
  {
    path: APP_ROUTER.KPI_INITIATIVES,
    element: <KpiInitiatives title='施策タイムライン' />,
    permissions: DASHBOARD_READERS,
  },
  {
    path: APP_ROUTER.APP_USAGE,
    element: <AppUsage title='アプリ利用状況' />,
    permissions: DASHBOARD_READERS,
  },
  {
    path: APP_ROUTER.COMPANY_REPORT,
    element: <CompanyReport title='企業別ダッシュボード' />,
    permissions: DASHBOARD_READERS,
  },
  {
    path: APP_ROUTER.USER.LIST,
    element: <UserPage title='ユーザー一覧' />,
  },
  {
    path: APP_ROUTER.USER.DETAIL,
    element: <UserDetail title='ユーザー一覧' />,
  },
  {
    path: APP_ROUTER.USER.UPDATE,
    element: <UserUpdatePage title='ユーザー一覧' />,
  },
  {
    path: APP_ROUTER.POST.LIST,
    element: <PostPage title='投稿一覧' />,
  },
  {
    path: APP_ROUTER.POST.DETAIL,
    element: <PostDetail title='投稿一覧' />,
  },
  {
    path: APP_ROUTER.POST_FUKUMARU.LIST,
    element: <PostFukumaruList title='ふくまる' />,
  },
  {
    path: APP_ROUTER.POST_FUKUMARU.DETAIL,
    element: <PostFukumaruDetail title='ふくまる' />,
  },
  {
    path: APP_ROUTER.SPECIAL_DAY.LIST,
    element: <SpecialDayList title='今日は何の日' />,
  },
  {
    path: APP_ROUTER.SPECIAL_DAY.DETAIL,
    element: <SpecialDayDetail title='今日は何の日' />,
  },
  {
    path: APP_ROUTER.SPECIAL_DAY.CREATE,
    element: <SpecialDayForm title='今日は何の日' />,
  },
  {
    path: APP_ROUTER.SPECIAL_DAY.UPDATE,
    element: <SpecialDayForm title='今日は何の日' />,
  },
  {
    path: APP_ROUTER.NOTIFICATION.LIST,
    element: <NotificationList title='お知らせ' />,
  },
  {
    path: APP_ROUTER.PROMPT.LIST,
    element: <PromptList title='プロンプト' />,
  },
  {
    path: APP_ROUTER.PROMPT.DETAIL,
    element: <PromptDetail title='プロンプト' />,
  },
  {
    path: APP_ROUTER.PROMPT.CREATE,
    element: <PromptForm title='プロンプト' />,
  },
  {
    path: APP_ROUTER.AYUMI.LIST,
    element: <AyumiList title='あゆみ' />,
  },
  {
    path: APP_ROUTER.AYUMI.CREATE,
    element: <AyumiForm title='あゆみ' />,
  },
  {
    path: APP_ROUTER.AYUMI.UPDATE,
    element: <AyumiForm title='あゆみ' />,
  },
  {
    path: APP_ROUTER.EVENT.LIST,
    element: <EventList title='イベント' />,
  },
  {
    path: APP_ROUTER.BANNER.LIST,
    element: <BannerList title='トップバナー' />,
  },
  {
    path: APP_ROUTER.CHAT_FUKUMARU.LIST,
    element: <ChatFukumaruList title='トーク - フクマル' />,
  },
  {
    path: APP_ROUTER.CONTENT.LIST,
    element: <ContentList title='コンテンツ' />,
  },
  {
    path: APP_ROUTER.SURVEY.LIST,
    element: <SurveyList title='アンケート' />,
  },
  {
    path: APP_ROUTER.NOTIFICATION_REMIND.LIST,
    element: <NotificationRemindList title='プッシュ通知' />,
  },
  {
    path: APP_ROUTER.SURVEY_HEALTH.LIST,
    element: <SurveyHealthList title='お口アンケート' />,
  },
  {
    path: APP_ROUTER.SURVEY_HEALTH.LIST_QUESTION,
    element: <SurveyHealthQuestionList title='お口アンケート' />,
  },
  {
    path: APP_ROUTER.VERSION.LIST,
    element: <VersionList title='バージョン' />,
  },
  {
    path: APP_ROUTER.ADMIN_USER.LIST,
    element: <AdminUserList title='管理ユーザー' />,
  },
  {
    path: APP_ROUTER.CALENDAR_POST.LIST,
    element: <CalendarPostList title='カレンダー投稿' />,
  },
  {
    path: APP_ROUTER.FUKUMARU_QUIZ.LIST,
    element: <FukumaruQuizList title='フクマルクイズ' />,
  },
  {
    path: APP_ROUTER.PARTNER.LIST,
    element: <PartnerList title='企業プロフィール' />,
  },
  {
    path: APP_ROUTER.COMPANY.LIST,
    element: <CompanyList title='企業' />,
  },
  {
    path: APP_ROUTER.COMPANY.DETAIL,
    element: <CompanyDetail title='団体詳細' />,
  },
  {
    path: APP_ROUTER.COMPANY.CREATE,
    element: <CompanyCreate title='団体の追加' />,
  },
  {
    path: APP_ROUTER.CATEGORY.LIST,
    element: <CategoryList title='カテゴリ' />,
  },
  {
    path: APP_ROUTER.ILLUSTRATION_THEME.LIST,
    element: <IllustrationThemeList title='イラストテーマ' />,
  },
  {
    path: APP_ROUTER.COGEVO_STAMP_IMAGE.LIST,
    element: <CogevoStampImageList title='CogEvoスタンプ画像' />,
  },
  {
    path: APP_ROUTER.SCREEN_MEDIA.LIST,
    element: <ScreenMediaList title='画面メディア' />,
  },
  {
    path: APP_ROUTER.OFFER.LIST,
    element: <OfferList title='オファー' />,
  },
  {
    path: APP_ROUTER.ARTICLE.LIST,
    element: <ArticleList title='記事' />,
  },
  {
    path: APP_ROUTER.MENU.INQUIRY,
    element: <InquiryList title='お問い合わせ' />,
  },
  {
    path: APP_ROUTER.GAME_INTRODUCTION.LIST,
    element: <GameIntroductionList title='家族のイマのゲーム紹介' />,
  },
  {
    path: APP_ROUTER.KAZOKUGOTO.GENERATE,
    element: <KazokugotoGenerate title='かぞくごと。生成' />,
  },
  {
    path: APP_ROUTER.KAZOKUGOTO.PROMPTS,
    element: <KazokugotoPromptList title='かぞくごと。プロンプト' />,
  },
  {
    path: APP_ROUTER.KAZOKUGOTO.CHARACTERS,
    element: <KazokugotoCharacterList title='かぞくごと。キャラクター' />,
  },
  {
    path: APP_ROUTER.KAZOKUGOTO.ITEMS,
    element: <KazokugotoItemList title='かぞくごと。アイテム一覧' />,
  },
  {
    path: APP_ROUTER.KAZOKUGOTO.TOPICS,
    element: <KazokugotoTopicList title='かぞくごと。365日マスタ' />,
  },
  {
    path: APP_ROUTER.AGENT.RUN,
    element: <AgentRun title='AIエージェント実行' />,
  },
  {
    path: APP_ROUTER.AGENT.MESSAGES,
    element: <AgentMessageList title='AIエージェント生成一覧' />,
  },
  {
    path: APP_ROUTER.AGENT.PROMPTS,
    element: <AgentPromptList title='AIエージェント プロンプト管理' />,
  },
  {
    path: APP_ROUTER.AGENT.FLOW,
    element: <AgentFlow title='AIエージェント フロー解説' />,
  },
  {
    path: APP_ROUTER.AGENT.HENKA,
    element: <AgentHenkaCoverage title='変化のたより 対象と実績' />,
  },
  {
    path: APP_ROUTER.FAMILY.LIST,
    element: <FamilyList title='家族一覧' />,
  },
];

export const PublicRouteWrap: React.FC<IRouteWrapProps> = ({
  isAuthenticated,
}) => {
  const location = useLocation();

  return isAuthenticated ? (
    <Navigate to={APP_ROUTER.HOME} state={{ from: location }} replace />
  ) : (
    <Outlet />
  );
};

export const PrivateRouteWrap: React.FC<IRouteWrapProps> = ({
  isAuthenticated,
}) => {
  const location = useLocation();

  return isAuthenticated ? (
    <Outlet />
  ) : (
    <Navigate to={APP_ROUTER.AUTH.LOGIN} state={{ from: location }} replace />
  );
};

/**
 * 権限の無いページを開いたらトップへ戻す（トップが権限に応じた遷移先を選ぶ）。
 * currentUser は Cookie から effect で復元されるので、復元前は何も描かない。
 */
export const RequirePermission: React.FC<{
  permissions?: PermissionKey[];
  children: React.ReactNode;
}> = ({ permissions = ADMIN_ONLY, children }) => {
  const { currentUser } = useAuth();

  if (!permissions.length) return <>{children}</>;
  if (!currentUser) return null;
  if (!hasAnyPermission(currentUser, permissions)) {
    return <Navigate to={APP_ROUTER.HOME} replace />;
  }

  return <>{children}</>;
};
